import { observable, action, computed } from "mobx";
import { bytesToSize, Task } from "@citychallenge/common";
import { DownloadTask } from "./download-task";

interface DownloadStoreOptions {
  totalBytes?: number;
}

export class DownloadStore {
  @observable
  public readonly all: DownloadTask[] = [];

  constructor(all: DownloadTask[], private options: DownloadStoreOptions = {}) {
    this.all = all;
  }

  @computed
  public get allDownloadTasks(): Task[] {
    return this.all.map(({ download }) => download);
  }

  @computed
  public get allCheckFileTasks(): Array<DownloadTask["checkFile"]> {
    return this.all.map(({ checkFile }) => checkFile);
  }

  @computed
  public get failedDownloadTasks(): DownloadTask[] {
    return this.all.filter(({ state }) => !!state.error);
  }

  @computed public get writtenMB(): string {
    return bytesToSize(this.totalBytesWritten, "MB", 0);
  }

  @computed public get totalMB(): string {
    return bytesToSize(this.totalBytes, "MB", 0);
  }

  @computed public get isEstimated(): boolean {
    return !!this.all.find(({ state }) => state.totalBytesEstimated);
  }

  @computed public get totalBytesWritten(): number {
    return this.all.reduce((val, { state }) => {
      return val + state.bytesWritten;
    }, 0);
  }

  @computed public get totalBytes(): number {
    if (this.options.totalBytes) return this.options.totalBytes;
    return this.all.reduce((val, { state }) => {
      return val + state.totalBytes;
    }, 0);
  }

  @computed public get completed() {
    return (
      this.all.filter(({ state }) => state.completed).length === this.all.length
    );
  }

  @computed
  public get totalProgress() {
    if (this.completed) return 100;
    if (!this.totalBytes) return 100;
    return (this.totalBytesWritten / this.totalBytes) * 100;
  }

  @computed
  public get allHaveReceivedProgess(): boolean {
    const hasReceivedIncompleteProgress = !!this.all.find(
      ({ state }) => state.hasReceivedProgess && state.progress < 100
    );

    const allHaveReceivedProgess = !this.all.find(
      ({ state }) => !state.hasReceivedProgess
    );

    return hasReceivedIncompleteProgress || allHaveReceivedProgess;
  }

  /**
   * Property is true if any if it's downloads has received progress
   */
  @computed
  public get hasReceivedProgess(): boolean {
    return !!this.all.find(({ state }) => state.hasReceivedProgess);
  }

  @computed
  public get totalSizeKnown(): boolean {
    return !this.all.filter(({ state }) => !state.totalBytes).length;
  }

  @computed
  public get allFilesChecked(): boolean {
    const uncheckedFiles = this.all.filter(({ state }) => !state.isFileChecked);
    return !uncheckedFiles.length;
  }

  @action
  public reset() {
    for (const { state, destroy } of this.all) {
      state.reset();
      destroy?.();
    }
  }
}
